import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { Notification } from './notification.model';
import { NotificationService } from './notification.service';
import { TbSeverityChipComponent } from './tb-severity-chip.component';

interface ToastItem {
  id: string;
  n: Notification;
  timeoutHandle: ReturnType<typeof setTimeout>;
}

const MAX_VISIBLE = 3;
const DURATION_MS: Record<string, number> = {
  INFO: 6000,
  WARN: 6000,
  CRITICAL: 12000,
};

/**
 * Toast popup tray — bottom-right, max 3 visible, surplus queued (DESIGN.md §7.8).
 * CRITICAL toasts require explicit dismiss (no auto-dismiss).
 * Subscribes to NotificationService arrival callbacks.
 * Place once in each app shell.
 */
@Component({
  selector: 'tb-notification-toast',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TbSeverityChipComponent],
  template: `
    <div class="tray" role="region" aria-live="polite" aria-label="Notifications">
      @for (item of visible(); track item.id) {
        <div
          class="toast"
          [attr.data-sev]="item.n.severity"
          role="alert"
          [attr.aria-atomic]="true"
        >
          <div class="toast-header">
            <tb-severity-chip [severity]="item.n.severity" />
            <span class="toast-title">{{ item.n.summary }}</span>
            <button
              class="toast-close"
              aria-label="Close notification"
              (click)="close(item.id)"
            >×</button>
          </div>
          @if (item.n.actionable && item.n.actionRoute) {
            <button class="toast-action" (click)="navigate(item)">View →</button>
          }
        </div>
      }
    </div>
  `,
  styles: `
    .tray {
      position: fixed; right: 1rem; bottom: 1rem; z-index: 9000;
      display: flex; flex-direction: column; gap: 0.5rem;
      pointer-events: none;
    }
    .toast {
      pointer-events: all;
      background: var(--tb-surface-panel, #1a1d24);
      border: 1px solid var(--tb-border-line, #2a2f3a);
      border-radius: var(--tb-radius-md, 12px);
      padding: 0.6rem 0.75rem;
      min-width: 280px; max-width: 380px;
      box-shadow: 0 4px 16px rgba(0,0,0,.45);
      animation: tb-toast-in 120ms ease;
      border-left: 3px solid transparent;
    }
    .toast[data-sev='INFO']     { border-left-color: var(--tb-status-info, #60a5fa); }
    .toast[data-sev='WARN']     { border-left-color: var(--tb-finance-pending, #e0af68); }
    .toast[data-sev='CRITICAL'] { border-left-color: var(--tb-finance-negative, #f7768e); }

    @keyframes tb-toast-in {
      from { opacity: 0; transform: translateY(16px); }
      to   { opacity: 1; transform: translateY(0); }
    }

    .toast-header { display: flex; align-items: center; gap: 0.5rem; }
    .toast-title { flex: 1; font-size: 0.86rem; font-weight: 600; }
    .toast-close {
      background: none; border: none; cursor: pointer;
      color: var(--tb-text-muted, #9aa5b1); font-size: 1rem; padding: 0;
      line-height: 1;
    }
    .toast-close:hover { color: var(--tb-text-primary, #e6e9ef); }

    .toast-action {
      background: none; border: none; cursor: pointer; padding: 0;
      margin-top: 0.35rem;
      font-size: 0.8rem; color: var(--tb-brand-accent, #4f86f7);
      text-decoration: underline;
    }

    /* Wave A: at sm (≤ 600 px) toasts are full-width at the bottom of the screen */
    @media (max-width: 600px) {
      .tray {
        bottom: 0; right: 0; left: 0;
        padding: 0.5rem;
        padding-bottom: max(0.5rem, env(safe-area-inset-bottom));
        align-items: stretch;
      }
      .toast {
        min-width: unset; max-width: unset; width: 100%;
        border-radius: var(--tb-radius-sm, 8px);
        animation: tb-toast-in-mobile 200ms ease;
      }
      @keyframes tb-toast-in-mobile {
        from { opacity: 0; transform: translateY(16px); }
        to   { opacity: 1; transform: translateY(0); }
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .toast { animation: none; }
    }
  `,
})
export class TbNotificationToastComponent implements OnInit, OnDestroy {
  private readonly notifService = inject(NotificationService);
  private readonly router = inject(Router);

  readonly visible = signal<ToastItem[]>([]);
  private queue: Notification[] = [];

  private readonly arrivalCb = (n: Notification) => this.push(n);

  ngOnInit(): void {
    this.notifService.onArrival(this.arrivalCb);
  }

  ngOnDestroy(): void {
    this.notifService.offArrival(this.arrivalCb);
    for (const t of this.visible()) clearTimeout(t.timeoutHandle);
  }

  private push(n: Notification): void {
    if (this.visible().length >= MAX_VISIBLE) {
      this.queue.push(n);
      return;
    }
    this.show(n);
  }

  private show(n: Notification): void {
    const isCritical = n.severity === 'CRITICAL';
    const duration = DURATION_MS[n.severity] ?? 6000;
    const id = `${n.id}-${Date.now()}`;

    let timeoutHandle: ReturnType<typeof setTimeout> = undefined as unknown as ReturnType<typeof setTimeout>;
    if (!isCritical) {
      timeoutHandle = setTimeout(() => this.close(id), duration);
    }

    const item: ToastItem = { id, n, timeoutHandle };
    this.visible.update((v) => [...v, item]);
  }

  close(id: string): void {
    this.visible.update((v) => {
      const item = v.find((t) => t.id === id);
      if (item?.timeoutHandle) clearTimeout(item.timeoutHandle);
      return v.filter((t) => t.id !== id);
    });
    // drain queue
    if (this.queue.length > 0) {
      const next = this.queue.shift()!;
      this.show(next);
    }
  }

  navigate(item: ToastItem): void {
    this.close(item.id);
    if (item.n.actionRoute) void this.router.navigateByUrl(item.n.actionRoute);
  }
}
